/**
 * @fileoverview View component of the ReduxModal, don't have any logic inside and completely controled by container component
 */
import React, { useState } from 'react'
import Modal from '@material-ui/core/Modal'
import { makeStyles } from '@material-ui/core/styles'

import SubmitButton from 'Components/Form/SubmitButton'
import CloseIcon from 'Assets/Images/icons/modalClose'

import { ModalHeader } from '../../../Components/Modal/ModalHeader'
import { ModalTitle } from '../../../Components/Modal/ModalTitle'
import { ModalBody } from '../../../Components/Modal/ModalBody'
import { ModalFooter } from '../../../Components/Modal/ModalFooter'

const ASPECTS = {
  scrollable: {
    modalWrapper: {
      flexGrow: 1,
    },
    container: {
      width: 600,
      background: 'white',
      borderRadius: '4px',
      boxShadow: 'none',
      height: 'min-content',
      margin: '100px auto',
      maxHeight: '88vh',
      overflow: 'auto',
    },
  },
  chat: {
    modalWrapper: {
      flexGrow: 1,
    },
    container: {
      width: 800,
      background: 'white',
      borderRadius: '4px',
      boxShadow: 'none',
      height: 'min-content',
      margin: '100px auto',
      maxHeight: '80vh',
      overflow: 'scroll',
    },
  },
  nonScrollable: {
    modalWrapper: {
      flexGrow: 1,
    },
    container: {
      width: 600,
      background: 'white',
      borderRadius: '4px',
      boxShadow: 'none',
      height: 'min-content',
      margin: '100px auto',
      maxHeight: '70vh',
      paddingBottom: '2px',
    },
  },
  pdfViewer: {
    modalWrapper: {
      flexGrow: 1,
    },
    container: {
      width: 800,
      background: 'white',
      borderRadius: '4px',
      boxShadow: 'none',
      height: 'min-content',
      margin: '50px auto',
      maxHeight: '88vh',
      paddingBottom: '20px',
      paddingTop: '20px',
    },
  },

  contract: {
    modalWrapper: {
      flexGrow: 1,
    },
    container: {
      width: 700,
      background: 'white',
      borderRadius: '4px',
      boxShadow: 'none',
      height: 'min-content',
      margin: '100px auto',
      maxHeight: '90vh',
      paddingBottom: '2px',
    },
  },

  conversions: {
    modalWrapper: {
      flexGrow: 1,
    },
    container: {
      width: 'max-content',
      minWidth: 632,
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0px 2px 14px rgba(39, 55, 84, 0.08)',
      height: 'min-content',
      margin: '100px auto',
    },
    header: {
      padding: '24px 24px 0px 24px',
      textAlign: 'right',
    },
    title: {
      marginTop: '32px',
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '30px',
    },
    close: {
      cursor: 'pointer',
    },
    body: {
      margin: '10px 30px',
    },
    footer: {
      padding: '30px 30px 56px',
    },
    submit: {
      width: '204px',
      height: '48px',
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
      borderRadius: '16px',
    },
  },
}

const useStyles = selectedAspect =>
  makeStyles(theme => ASPECTS[selectedAspect])()

/**
 * Renders styled modal view for ReduxModal
 *
 * @param {boolean} props.show Indicates whether modal should be visible
 * @param {string} props.title Modal's title
 * @param {string} props.titleFontSize font size desired for the title with unit like 16px or 2rem
 * @param {function} props.handleConfirm Action of the confirm button
 * @param {string} props.confirmLabel Label of the confirm button
 * @param {function} props.handleCancel Action of the cancel button
 * @param {string} props.cancelLabel Label of the cancel button
 * @param {array} props.children Component's children that will be rendered in the middle of the view
 * @param {boolean} props.confirmDisabled Indicates whether confirm button should be disabled
 * @param {boolean} props.hideCancel Indicates whether cancel button should be hidden
 * @param {boolean} props.hideTitle Indicates whether title should be hidden
 * @param {boolean} props.hideButtons Indicates whether all footer buttons should be hidden
 * @param {string} props.aspect Indicates which aspect of the modal should be used
 *
 * @returns {JSX.Element}
 */
const ReduxModalView = ({
  show,
  title,
  titleFontSize,
  handleConfirm,
  confirmLabel = 'Confirm',
  confirmVariant = 'contained',
  handleCancel,
  cancelLabel = 'Cancel',
  cancelVariant = 'contained',
  children,
  confirmDisabled,
  hideCancel = false,
  hideTitle = false,
  hideButtons = false,
  submitButtonCypressId = '',
  aspect = 'scrollable',
  isReportModal = false,
  container = null,
  onlyClickOnce = false,
  hideReduxModal,
}) => {
  const classes = useStyles(aspect)
  const [clicked, setClicked] = useState(false)
  const onSubmit = () => {
    if (!clicked) {
      setClicked(true)
    }
    handleConfirm()
  }
  const onCancel = () => {
    if (!clicked) {
      setClicked(true)
    }
    handleCancel()
  }

  return (
    <Modal
      open={show}
      disableEnforceFocus
      className={classes.modalWrapper}
      container={container}
      onClose={(props, reason) =>
        ['backdropClick', 'escapeKeyDown'].includes(reason) && hideReduxModal()
      }
    >
      <div className={classes.container}>
        {!hideTitle && (
          <ModalHeader className={classes.header}>
            {isReportModal && (
              <span className={classes.close} onClick={handleCancel}>
                <CloseIcon />
              </span>
            )}
            {title && (
              <ModalTitle
                style={titleFontSize && { fontSize: titleFontSize }}
                className={classes.title}
              >
                {title}
              </ModalTitle>
            )}
          </ModalHeader>
        )}
        <ModalBody className={classes.body}>{children}</ModalBody>
        {!hideButtons && (
          <ModalFooter className={classes.footer}>
            {!hideCancel && (
              <SubmitButton
                value="cancel"
                label={cancelLabel}
                onClick={onCancel}
                disabled={clicked && onlyClickOnce}
                variant={cancelVariant}
              />
            )}
            <SubmitButton
              className={classes.submit}
              disabled={confirmDisabled || (clicked && onlyClickOnce)}
              value={confirmLabel}
              onClick={onSubmit}
              cypress_id={submitButtonCypressId}
              variant={confirmVariant}
            />
          </ModalFooter>
        )}
      </div>
    </Modal>
  )
}

export default ReduxModalView
